import { api } from "@/lib/api";

// Shared per-user avatar-config cache so the <Avatar> component fetches each
// user's config at most once, and live WS updates fan out to every instance.
const cache = new Map();     // user_id -> { avatar_type, avatar_url, dicebear_style }
const inflight = new Map();  // user_id -> Promise
const subs = new Map();      // user_id -> Set<fn>

function emit(id, cfg) {
  const set = subs.get(id);
  if (set) set.forEach((fn) => fn(cfg));
}

export function getCached(id) {
  return id ? cache.get(id) : undefined;
}

// Seed the cache from a user object that already carries avatar fields
// (e.g. the workspace members list), avoiding a fetch.
export function prime(id, cfg) {
  if (!id || !cfg) return;
  cache.set(id, cfg);
  emit(id, cfg);
}

export async function fetchAvatar(id) {
  if (!id) return null;
  if (cache.has(id)) return cache.get(id);
  if (inflight.has(id)) return inflight.get(id);
  const p = api.get(`/users/${id}/avatar`)
    .then((r) => {
      const cfg = {
        avatar_type: r.data.avatar_type,
        avatar_url: r.data.avatar_url,
        dicebear_style: r.data.dicebear_style,
      };
      cache.set(id, cfg);
      inflight.delete(id);
      emit(id, cfg);
      return cfg;
    })
    .catch(() => { inflight.delete(id); return null; });
  inflight.set(id, p);
  return p;
}

export function subscribe(id, fn) {
  if (!subs.has(id)) subs.set(id, new Set());
  subs.get(id).add(fn);
  return () => { const s = subs.get(id); if (s) s.delete(fn); };
}

// Apply a user_avatar_updated WS event.
export function applyWsUpdate(ev) {
  if (!ev?.user_id) return;
  prime(ev.user_id, {
    avatar_type: ev.avatar_type,
    avatar_url: ev.avatar_url,
    dicebear_style: ev.dicebear_style,
  });
}
